import { useEffect, useId, useRef, type FC } from "react";
import { useScroll } from "../../../contexts/useScroll";

interface ChatMessageListOptionsProps {
  options: string[];
  onClick: (option: string) => void;
}

const ChatMessageListOptions: FC<ChatMessageListOptionsProps> = ({ options, onClick }) => {
  const { scrollToBottomIfNearBottom } = useScroll();
  const id = useId();
  const rootRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    // `setTimeout` used to ensure DOM is updated before scroll attempt.
    // Conditional: the options mount once the answer above has finished
    // typing, and a reader still at the top of a long answer must not be
    // pulled to its end (LAS-1381). Own height passed so "near the bottom"
    // means the bottom as it was before the options appeared.
    setTimeout(() => scrollToBottomIfNearBottom(rootRef.current?.offsetHeight ?? 0), 0);
  }, [scrollToBottomIfNearBottom]);

  if (options.length === 0) {
    return null;
  }

  return (
    <div ref={rootRef} className="flex justify-end pt-6">
      <div className="flex flex-col space-y-1 items-end">
        {options.map((option, i) => (
          <button
            type="button"
            key={`${id}-${i}`}
            onClick={() => onClick(option)}
            className="py-2 px-4 rounded-lg rounded-tr-none bg-lastbot-surface-muted text-right text-sm text-lastbot-on-surface-soft transform opacity-0 animate-fade-in cursor-pointer"
            style={{ animationDuration: `${300 + i * 150}ms` }}
            data-testid={`assistant-message-option`}
          >
            {option}
          </button>
        ))}
      </div>
    </div>
  );
};

export default ChatMessageListOptions;
